import React from 'react';

import { ChevronDown } from 'lucide-react';

import { InvitationWizardData } from '../../../types/invitation';

interface Step2WorkInfoProps {
  wizardData: InvitationWizardData;
  errors: Record<string, string>;
  onInputChange: (field: string, value: string) => void;
}

const industryOptions = [
  'architecture',
  'urban planning',
  'light designer',
  'electrical engineering',
  'lighting manufacturer',
  'interior design',
  'civil engineering',
  'facility management',
  'marketing',
  'other',
];
const departmentOptions = ['design', 'strategy', 'development', 'other'];
const roleOptions = ['director', 'designer', 'other'];

const baseInput =
  'w-full bg-neutral-900 border text-base text-white rounded-lg px-3 py-2 outline-none transition-colors';

const fieldClass = (hasError: boolean) =>
  `${baseInput} ${hasError ? 'border-[#FF8A00]' : 'border-neutral-700 focus:border-green01'}`;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

interface SelectFieldProps {
  label: string;
  value: string;
  options: string[];
  error?: string;
  onChange: (value: string) => void;
}

const SelectField: React.FC<SelectFieldProps> = ({ label, value, options, error, onChange }) => (
  <div>
    <label className="block text-xs text-white/70 mb-1">{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${fieldClass(!!error)} pr-9 appearance-none cursor-pointer`}
      >
        <option value="" disabled className="bg-neutral-900">
          Select {label.toLowerCase()}
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-neutral-900">
            {capitalize(opt)}
          </option>
        ))}
      </select>
      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
    </div>
    {error && <p className="text-[#FF8A00] text-xs mt-1 ml-0.5">{error}</p>}
  </div>
);

export const Step2WorkInfo: React.FC<Step2WorkInfoProps> = ({ wizardData, errors, onInputChange }) => {
  return (
    <div className="flex flex-col gap-3 md:gap-3.5 mx-4">
      {/* Company Name */}
      <div>
        <label className="block text-xs text-white/70 mb-1">Company Name</label>
        <input
          type="text"
          value={wizardData.companyName}
          onChange={(e) => onInputChange('companyName', e.target.value)}
          className={fieldClass(!!errors.companyName)}
        />
        {errors.companyName && <p className="text-[#FF8A00] text-xs mt-1 ml-0.5">{errors.companyName}</p>}
      </div>

      <SelectField
        label="Industry"
        value={wizardData.industry}
        options={industryOptions}
        error={errors.industry}
        onChange={(v) => onInputChange('industry', v)}
      />

      <SelectField
        label="Department"
        value={wizardData.department}
        options={departmentOptions}
        error={errors.department}
        onChange={(v) => onInputChange('department', v)}
      />

      <SelectField
        label="Role"
        value={wizardData.role}
        options={roleOptions}
        error={errors.role}
        onChange={(v) => onInputChange('role', v)}
      />
    </div>
  );
};
